/**
 * In-game animation debug console.
 * Toggle: backtick `  or  F2
 * Copy logs for pasting into a bug report.
 */

export type LogLevel = 'info' | 'warn' | 'error' | 'anim' | 'bone';

export interface AnimDebugSnapshot {
  t: number;
  action: string;
  actionT: number;
  blade: number;
  stance: number;
  loco: Record<string, number>;
  combat: Record<string, number>;
  bones: Record<string, { ex: number; ey: number; ez: number; qx: number; qy: number; qz: number; qw: number }>;
  notes: string[];
}

type LogLine = { t: number; level: LogLevel; msg: string };

const MAX_LINES = 400;
const MAX_SNAPSHOTS = 120;

class AnimDebugConsole {
  private enabled = false;
  private lines: LogLine[] = [];
  private snapshots: AnimDebugSnapshot[] = [];
  private start = performance.now();
  private panel: HTMLElement | null = null;
  private logEl: HTMLElement | null = null;
  private statsEl: HTMLElement | null = null;
  private dirty = false;
  private lastUi = 0;
  private frame = 0;
  /** Sticky flags / one-shot notes for the session */
  private notes = new Set<string>();

  constructor() {
    if (typeof window === 'undefined') return;
    // Auto-open if URL has ?debug=anim
    const q = new URLSearchParams(window.location.search);
    if (q.get('debug') === 'anim' || q.get('animdebug') === '1') {
      this.setEnabled(true);
    }
    window.addEventListener('keydown', this.onKey);
    // Expose for console: window.__animDebug
    (window as unknown as { __animDebug: AnimDebugConsole }).__animDebug = this;
  }

  private onKey = (e: KeyboardEvent): void => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (e.key === '`' || e.key === 'F2') {
      e.preventDefault();
      this.toggle();
    }
  };

  toggle(): void {
    this.setEnabled(!this.enabled);
  }

  setEnabled(on: boolean): void {
    this.enabled = on;
    if (on) {
      this.ensurePanel();
      this.panel?.classList.remove('hidden');
      this.log('info', 'Anim debug ON — F2 or ` to close · Copy dumps state for bug reports');
    } else {
      this.panel?.classList.add('hidden');
    }
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  note(flag: string): void {
    if (!this.notes.has(flag)) {
      this.notes.add(flag);
      this.log('info', flag);
    }
  }

  log(level: LogLevel, msg: string): void {
    const t = (performance.now() - this.start) / 1000;
    this.lines.push({ t, level, msg });
    if (this.lines.length > MAX_LINES) this.lines.splice(0, this.lines.length - MAX_LINES);
    this.dirty = true;
    if (level === 'error' || level === 'warn') {
      // Always mirror important lines to browser console
      // eslint-disable-next-line no-console
      console[level === 'error' ? 'error' : 'warn'](`[anim ${(t).toFixed(2)}s] ${msg}`);
    } else if (this.enabled) {
      // eslint-disable-next-line no-console
      console.debug(`[anim ${(t).toFixed(2)}s] ${msg}`);
    }
  }

  info(msg: string): void {
    this.log('info', msg);
  }

  warn(msg: string): void {
    this.log('warn', msg);
  }

  error(msg: string): void {
    this.log('error', msg);
  }

  anim(msg: string): void {
    this.log('anim', msg);
  }

  pushSnapshot(s: AnimDebugSnapshot): void {
    this.snapshots.push(s);
    if (this.snapshots.length > MAX_SNAPSHOTS) {
      this.snapshots.splice(0, this.snapshots.length - MAX_SNAPSHOTS);
    }
    this.dirty = true;
  }

  /** Call once per frame when enabled (or every N frames for telemetry). */
  tickUi(force = false): void {
    this.frame++;
    if (!this.enabled || !this.panel) return;
    const now = performance.now();
    if (!force && now - this.lastUi < 100 && !this.dirty) return;
    this.lastUi = now;
    this.dirty = false;
    this.render();
  }

  private ensurePanel(): void {
    if (this.panel) return;
    const root = document.createElement('div');
    root.id = 'anim-debug';
    root.innerHTML = `
      <div class="anim-debug-head">
        <strong>Anim Debug</strong>
        <span class="anim-debug-hint">F2 / \` toggle</span>
        <div class="anim-debug-actions">
          <button type="button" data-act="copy">Copy all</button>
          <button type="button" data-act="copy-snap">Copy snapshot</button>
          <button type="button" data-act="clear">Clear</button>
          <button type="button" data-act="close">×</button>
        </div>
      </div>
      <pre class="anim-debug-stats" id="anim-debug-stats"></pre>
      <div class="anim-debug-log" id="anim-debug-log"></div>
    `;
    document.body.appendChild(root);
    this.panel = root;
    this.statsEl = root.querySelector('#anim-debug-stats');
    this.logEl = root.querySelector('#anim-debug-log');
    root.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest('button[data-act]') as HTMLButtonElement | null;
      if (!btn) return;
      const act = btn.dataset.act;
      if (act === 'copy') void this.copyText(this.dumpAll());
      if (act === 'copy-snap') void this.copyText(this.dumpLatestSnapshot());
      if (act === 'clear') {
        this.lines = [];
        this.snapshots = [];
        this.dirty = true;
        this.render();
      }
      if (act === 'close') this.setEnabled(false);
    });
  }

  private render(): void {
    if (!this.logEl || !this.statsEl) return;
    const snap = this.snapshots[this.snapshots.length - 1];
    if (snap) {
      const loco = Object.entries(snap.loco)
        .map(([k, v]) => `${k}:${v.toFixed(2)}`)
        .join(' ');
      const combat = Object.entries(snap.combat)
        .filter(([, v]) => v > 0.01)
        .map(([k, v]) => `${k.replace('Combat_', '')}:${v.toFixed(2)}`)
        .join(' ');
      const boneLines = Object.entries(snap.bones)
        .map(([k, b]) => {
          return `${k} e(${b.ex.toFixed(2)},${b.ey.toFixed(2)},${b.ez.toFixed(2)}) q(${b.qx.toFixed(2)},${b.qy.toFixed(2)},${b.qz.toFixed(2)},${b.qw.toFixed(2)})`;
        })
        .join('\n');
      this.statsEl.textContent =
        `t=${snap.t.toFixed(2)}s  action=${snap.action} @${snap.actionT.toFixed(2)}\n` +
        `blade=${snap.blade.toFixed(2)} stance=${snap.stance.toFixed(2)}\n` +
        `loco ${loco || '(none)'}\n` +
        `combat ${combat || '(none)'}\n` +
        boneLines +
        (snap.notes.length ? `\nnotes: ${snap.notes.join('; ')}` : '');
    } else {
      this.statsEl.textContent = 'No snapshots yet — start a bout.';
    }

    const html = this.lines
      .slice(-80)
      .map((l) => {
        const cls = `lvl-${l.level}`;
        return `<div class="${cls}"><span class="ts">${l.t.toFixed(2)}</span> ${escapeHtml(l.msg)}</div>`;
      })
      .join('');
    this.logEl.innerHTML = html;
    this.logEl.scrollTop = this.logEl.scrollHeight;
  }

  dumpLatestSnapshot(): string {
    const snap = this.snapshots[this.snapshots.length - 1];
    if (!snap) return '(no snapshot)';
    return JSON.stringify(snap, null, 2);
  }

  dumpAll(): string {
    return JSON.stringify(
      {
        exportedAt: new Date().toISOString(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        url: typeof location !== 'undefined' ? location.href : '',
        notes: [...this.notes],
        log: this.lines,
        snapshots: this.snapshots.slice(-40),
      },
      null,
      2,
    );
  }

  private async copyText(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.log('info', `Copied ${text.length} chars to clipboard`);
      this.tickUi(true);
    } catch {
      // Fallback prompt
      window.prompt('Copy debug dump:', text.slice(0, 2000));
    }
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    this.panel?.remove();
    this.panel = null;
  }
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Singleton */
export const animDebug = new AnimDebugConsole();
